import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type {
  BoardPagesAndParams,
  EndUserRouteAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { Idea } from '@aurora/shared-generated/types/graphql-schema-types';
import type { EndUserPages } from '@aurora/shared-types/pages/enums';
import { EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import type {
  IdeaStatusInformationQuery,
  IdeaStatusInformationQueryVariables,
  MessageViewsQueryVariables
} from '../../../types/graphql-types';
import type { WidgetFC } from '../../common/Widget/types';
import AllMessagesList from '../../messages/MessageListForViewAllWidget/AllMessagesList/AllMessagesList';
import { MessageListTabItem } from '../../messages/MessageListTabs/MessageListTabs';
import type { MessageListWidgetExtendedProps } from '../../messages/types';
import useQueryParamValueForWidget from '../../useQueryParamValueForWidget';
import ideaStatusPropertiesQuery from '../IdeaStatusInformation.query.graphql';

/**
 *  Ideas list widget for View All Ideas page
 *
 *  @author Manasvini Arul
 */
const AllIdeasWidget: WidgetFC<MessageListWidgetExtendedProps> = ({
  viewVariant,
  listVariant,
  pagerVariant,
  pageSize,
  panelType
}) => {
  const {
    publicConfig: { messageStatusEnabled }
  } = useContext(TenantContext);
  const { router } = useEndUserRoutes();
  const { contextNode } = useContext(AppContext);

  const sortList: MessageListTabItem[] = [
    MessageListTabItem.TRENDING,
    MessageListTabItem.MOST_RECENT,
    MessageListTabItem.MOST_REPLIES,
    MessageListTabItem.MOST_VIEWED,
    MessageListTabItem.MOST_KUDOED,
    MessageListTabItem.NEWEST
  ];

  const { data: propertiesData, loading: ideaPropertiesLoading } = useQueryWithTracing<
    IdeaStatusInformationQuery,
    IdeaStatusInformationQueryVariables
  >(module, ideaStatusPropertiesQuery, {
    variables: {
      id: contextNode.id,
      useIdeaStatusProperties: true
    },
    skip: !messageStatusEnabled
  });

  const statusUrlValue = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_MESSAGE_STATUS,
    ''
  );
  const { key: statusParameterKey, value: statusParameterValue } = useQueryParamValueForWidget(
    EndUserQueryParams.SEARCH_FILTER_BY_MESSAGE_STATUS,
    statusUrlValue
  );

  if (statusUrlValue) {
    router.replaceRoute<EndUserRouteAndParams<EndUserPages>>(
      router.getCurrentPageName(),
      router.getPathParams<BoardPagesAndParams>(),
      {
        ...router.getQueryParams(),
        [EndUserQueryParams.SEARCH_FILTER_BY_MESSAGE_STATUS]: null,
        [statusParameterKey]: statusUrlValue
      },
      { scroll: false }
    );
  }

  if (ideaPropertiesLoading) {
    return null;
  }

  const isIdeaStatusEnabled = messageStatusEnabled
    ? (propertiesData?.coreNode as Idea)?.ideaStatusProperties?.isIdeaStatusEnabled
    : false;
  const queryVariables: MessageViewsQueryVariables = isIdeaStatusEnabled
    ? {
        constraints: {
          ...(statusParameterValue && { statusKey: { eq: statusParameterValue } })
        },
        useMessageStatus: true
      }
    : {};

  return (
    <AllMessagesList
      viewVariant={viewVariant}
      pagerVariant={pagerVariant}
      listVariant={listVariant}
      pageSize={pageSize}
      panelType={panelType}
      queryVariables={queryVariables}
      sortOptions={sortList}
    />
  );
};

export default AllIdeasWidget;
